<template>
    <transition name="debug-inspector-slide">
        <div v-if="visible" class="debug-inspector" :class="{ 'inspector-collapsed': collapsed }"
             :style="inspectorStyle">
            <!-- Resize handles -->
            <div v-if="!collapsed" class="resize-handle resize-right" @mousedown.prevent="startResize($event, 'right')"></div>
            <div v-if="!collapsed" class="resize-handle resize-bottom" @mousedown.prevent="startResize($event, 'bottom')"></div>
            <div v-if="!collapsed" class="resize-handle resize-corner" @mousedown.prevent="startResize($event, 'corner')">
                <v-icon size="22" class="resize-corner-icon">mdi-resize-bottom-right</v-icon>
            </div>
            <!-- Header -->
            <div class="di-header" :class="{ 'di-header-collapsed': collapsed }" @click="collapsed && (collapsed = false)">
                <div class="di-title">
                    <v-icon size="18" color="primary">mdi-bug-outline</v-icon>
                    <span v-if="!collapsed">Debug Inspector</span>
                </div>
                <div v-if="!collapsed" class="di-header-actions">
                    <v-btn icon size="x-small" variant="text" @click.stop="collapsed = true">
                        <v-icon size="16">mdi-chevron-left</v-icon>
                    </v-btn>
                </div>
            </div>

            <div v-if="!collapsed" class="di-body">
                <!-- Tabs -->
                <v-tabs v-model="activeTab" density="compact" color="primary" class="di-tabs">
                    <v-tab value="steps" size="small">
                        <v-icon size="14" start>mdi-format-list-numbered</v-icon>
                        Steps
                    </v-tab>
                    <v-tab value="context" size="small">
                        <v-icon size="14" start>mdi-view-dashboard-outline</v-icon>
                        Context
                    </v-tab>
                </v-tabs>

                <v-tabs-window v-model="activeTab" class="di-tab-content">
                    <!-- Steps Tab -->
                    <v-tabs-window-item value="steps">
                        <div v-if="!steps.length" class="di-empty">
                            <v-icon size="40" color="grey-lighten-1">mdi-debug-step-into</v-icon>
                            <div class="text-grey mt-2" style="font-size: 12px;">Send a message to start debugging</div>
                        </div>
                        <div v-else class="di-steps-list" ref="stepsList">
                            <template v-for="(step, idx) in steps" :key="idx">
                                <div class="di-step-item"
                                    :class="{
                                        'step-current': step.nodeId === currentNodeId && (step.eventType === 'node_start' || step.eventType === 'breakpoint_hit' || (step.eventType === 'agent_start' && !step.isSubStep)),
                                        'step-success': (step.eventType === 'node_complete' || step.eventType === 'agent_complete') && step.success,
                                        'step-error': step.eventType === 'node_error' || (step.eventType === 'node_complete' && !step.success) || (step.eventType === 'agent_complete' && !step.success),
                                        'step-paused': step.eventType === 'breakpoint_hit',
                                        'step-selected': selectedStepIndex === idx,
                                        'step-sub': step.isSubStep
                                    }"
                                    @click="selectStep(idx)">
                                    <div class="step-indicator">
                                        <v-icon v-if="step.eventType === 'node_start'" size="14" color="blue" class="spin-animation">mdi-loading</v-icon>
                                        <v-icon v-else-if="step.eventType === 'agent_start'" size="14" color="blue" class="spin-animation">mdi-loading</v-icon>
                                        <v-icon v-else-if="(step.eventType === 'node_complete' || step.eventType === 'agent_complete') && step.success" size="14" color="green">mdi-check-circle</v-icon>
                                        <v-icon v-else-if="step.eventType === 'node_error' || (step.eventType === 'agent_complete' && !step.success)" size="14" color="red">mdi-alert-circle</v-icon>
                                        <v-icon v-else-if="step.eventType === 'breakpoint_hit'" size="14" color="blue" class="spin-animation">mdi-loading</v-icon>
                                        <v-icon v-else size="14" color="grey">mdi-circle-outline</v-icon>
                                    </div>
                                    <div class="step-content">
                                        <div class="step-name">
                                            <span v-if="!step.isSubStep" class="step-index">{{ getStepDisplayIndex(idx) }}.</span>
                                            <v-icon v-if="step.isSubStep" size="12" color="#888" class="mr-1">mdi-subdirectory-arrow-right</v-icon>
                                            {{ step.nodeName || step.nodeType }}
                                        </div>
                                        <div class="step-meta">
                                            <v-chip size="x-small" :color="getNodeTypeColor(step.nodeType)" variant="tonal" class="mr-1">
                                                {{ step.nodeType }}
                                            </v-chip>
                                            <span v-if="step.durationMs" class="step-duration">{{ formatDuration(step.durationMs) }}</span>
                                        </div>
                                    </div>
                                    <v-icon v-if="hasStepDetail(step)" size="12" color="grey" class="step-expand-icon">
                                        {{ selectedStepIndex === idx ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                    </v-icon>
                                </div>
                                <!-- Inline detail accordion -->
                                <div v-if="selectedStepIndex === idx" class="di-step-detail-inline" :class="{ 'detail-agent': step.isSubStep }" @click.stop>
                                    <div v-if="step.agentName" class="detail-section">
                                        <div class="detail-label"><v-icon size="10" class="mr-1">mdi-robot</v-icon>Agent</div>
                                        <v-chip size="x-small" color="deep-purple" variant="tonal">{{ step.agentName }}</v-chip>
                                    </div>
                                    <div v-if="getToolsUsed(step).length" class="detail-section">
                                        <div class="detail-label"><v-icon size="10" class="mr-1">mdi-wrench</v-icon>Tools Used</div>
                                        <div class="tools-used-list">
                                            <v-chip v-for="(tool, ti) in getToolsUsed(step)" :key="ti"
                                                size="x-small" variant="tonal" class="mr-1 mb-1"
                                                :color="tool.startsWith('Knowledge:') ? 'teal' : 'blue'">
                                                <v-icon size="10" start>{{ tool.startsWith('Knowledge:') ? 'mdi-book-open-variant' : 'mdi-wrench-outline' }}</v-icon>
                                                {{ tool.replace('Knowledge:', '') }}
                                            </v-chip>
                                        </div>
                                    </div>
                                    <div v-if="getStepsExecuted(step).length" class="detail-section">
                                        <div class="detail-label"><v-icon size="10" class="mr-1">mdi-format-list-numbered</v-icon>Execution Steps</div>
                                        <div class="agent-steps-list">
                                            <div v-for="(execStep, si) in getStepsExecuted(step)" :key="si" class="agent-exec-step">
                                                <v-icon size="10" :color="execStep.Success || execStep.success ? 'green' : 'red'" class="mr-1">
                                                    {{ (execStep.Success || execStep.success) ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                                                </v-icon>
                                                <span class="exec-step-tool" v-if="execStep.ToolUsed || execStep.toolUsed">{{ execStep.ToolUsed || execStep.toolUsed }}</span>
                                                <span class="exec-step-desc">{{ execStep.StepDescription || execStep.stepDescription }}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="step.outputPath" class="detail-section">
                                        <div class="detail-label">Output Path</div>
                                        <v-chip size="x-small" color="info" variant="flat">{{ step.outputPath }}</v-chip>
                                    </div>
                                    <div v-if="step.gptReply" class="detail-section">
                                        <div class="detail-label">AI Response</div>
                                        <div class="detail-code">{{ step.gptReply }}</div>
                                    </div>
                                    <div v-if="getParallelItems(step).length" class="detail-section">
                                        <div class="detail-label"><v-icon size="10" class="mr-1">mdi-source-branch</v-icon>AI Parallel Results</div>
                                        <div class="parallel-result-list">
                                            <div v-for="(result, ri) in getParallelItems(step)" :key="ri" class="parallel-result-item">
                                                <div class="parallel-result-header">
                                                    <v-icon size="11" :color="getParallelResultColor(result)" class="mr-1" :class="{ 'spin-animation': getParallelResultStatus(result) === 'running' || getParallelResultStatus(result) === 'paused' }">
                                                        {{ getParallelResultIcon(result) }}
                                                    </v-icon>
                                                    <span class="parallel-result-name">{{ getParallelResultName(result, ri) }}</span>
                                                    <span v-if="getParallelResultDuration(result)" class="parallel-result-duration">{{ formatDuration(getParallelResultDuration(result)) }}</span>
                                                </div>
                                                <div v-if="getParallelResultResponse(result)" class="parallel-result-response">{{ getParallelResultResponse(result) }}</div>
                                                <div v-if="getParallelResultError(result)" class="parallel-result-response parallel-result-error">{{ getParallelResultError(result) }}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="step.errorMessage" class="detail-section">
                                        <div class="detail-label" style="color: #c62828;">Error</div>
                                        <div class="detail-code error-code">{{ step.errorMessage }}</div>
                                    </div>
                                    <div v-if="shouldShowStepVariables(step)" class="detail-section">
                                        <div class="detail-label">Variables at this step</div>
                                        <div class="detail-code">{{ formatJson(step.contextVariables) }}</div>
                                    </div>
                                    <div v-if="!hasStepDetail(step)" class="di-no-detail">
                                        <span class="text-grey" style="font-size: 11px;">No details available for this step</span>
                                    </div>
                                    <div v-if="canJumpToStep(step)" class="detail-section mt-2">
                                        <v-btn size="x-small" variant="tonal" color="orange" @click.stop="emitSetNext(step)" prepend-icon="mdi-debug-step-over">
                                            Jump to this node
                                        </v-btn>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </v-tabs-window-item>

                    <!-- Context Tab -->
                    <v-tabs-window-item value="context">
                        <div v-if="!Object.keys(currentVariables).length" class="di-empty">
                            <v-icon size="40" color="grey-lighten-1">mdi-view-dashboard-outline</v-icon>
                            <div class="text-grey mt-2" style="font-size: 12px;">Run the flow to see context data</div>
                        </div>
                        <div v-else class="ctx-container">
                            <div class="ctx-card" v-if="ctxInput.message">
                                <div class="ctx-card-header">
                                    <v-icon size="14" color="#1976d2">mdi-message-text-outline</v-icon>
                                    <span>Input</span>
                                    <v-chip v-if="isPaused && userMessageDirty" size="x-small" color="warning" variant="tonal" class="ml-auto">edited</v-chip>
                                </div>
                                <div class="ctx-card-body">
                                    <div class="ctx-row">
                                        <span class="ctx-label">Message</span>
                                        <v-textarea
                                            v-if="isPaused"
                                            :model-value="editedUserMessage"
                                            @update:model-value="onUserMessageInput"
                                            variant="outlined"
                                            density="compact"
                                            rows="2"
                                            auto-grow
                                            hide-details
                                            class="ctx-editable-input"
                                            placeholder="Edit user message..."
                                        />
                                        <span v-else class="ctx-value ctx-value-text">{{ ctxInput.message }}</span>
                                    </div>
                                </div>
                            </div>

                            <div class="ctx-card" v-if="ctxAI.response || ctxAI.model">
                                <div class="ctx-card-header">
                                    <v-icon size="14" color="#7b1fa2">mdi-robot-outline</v-icon>
                                    <span>AI Output</span>
                                </div>
                                <div class="ctx-card-body">
                                    <div class="ctx-row" v-if="ctxAI.response">
                                        <span class="ctx-label">Response</span>
                                        <div class="ctx-value ctx-value-prose" :class="{ 'ctx-expanded': aiResponseExpanded }">
                                            {{ ctxAI.response }}
                                        </div>
                                        <button v-if="aiResponseTextLength > 180" class="ctx-toggle" @click="aiResponseExpanded = !aiResponseExpanded">
                                            {{ aiResponseExpanded ? 'Show less' : 'Show more' }}
                                        </button>
                                    </div>
                                    <div class="ctx-row-inline" v-if="ctxAI.model">
                                        <span class="ctx-label">Model</span>
                                        <v-chip size="x-small" color="deep-purple" variant="tonal">{{ ctxAI.model }}</v-chip>
                                    </div>
                                    <div class="ctx-row-inline" v-if="ctxAI.tokens">
                                        <span class="ctx-label">Tokens</span>
                                        <div class="ctx-token-group">
                                            <span class="ctx-token total">{{ formatNumber(ctxAI.tokens.total_tokens || ctxAI.tokens.TotalTokens || 0) }} total</span>
                                            <span class="ctx-token prompt" v-if="ctxAI.tokens.prompt_tokens || ctxAI.tokens.PromptTokens">{{ formatNumber(ctxAI.tokens.prompt_tokens || ctxAI.tokens.PromptTokens) }}</span>
                                            <span class="ctx-token completion" v-if="ctxAI.tokens.completion_tokens || ctxAI.tokens.CompletionTokens">{{ formatNumber(ctxAI.tokens.completion_tokens || ctxAI.tokens.CompletionTokens) }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="ctx-card" v-if="ctxQueryRewriter.rewritten || ctxQueryRewriter.original || ctxQueryRewriter.mode">
                                <div class="ctx-card-header">
                                    <v-icon size="14" color="#7c3aed">mdi-message-text-clock-outline</v-icon>
                                    <span>Query Rewriter</span>
                                </div>
                                <div class="ctx-card-body">
                                    <div class="ctx-row-inline" v-if="ctxQueryRewriter.mode">
                                        <span class="ctx-label">Mode</span>
                                        <v-chip size="x-small" color="deep-purple" variant="tonal">{{ ctxQueryRewriter.mode }}</v-chip>
                                    </div>
                                    <div class="ctx-row" v-if="ctxQueryRewriter.original">
                                        <span class="ctx-label">Original</span>
                                        <span class="ctx-value ctx-value-text">{{ ctxQueryRewriter.original }}</span>
                                    </div>
                                    <div class="ctx-row" v-if="ctxQueryRewriter.continueQuestion">
                                        <span class="ctx-label">Continue Question</span>
                                        <span class="ctx-value ctx-value-text">{{ ctxQueryRewriter.continueQuestion }}</span>
                                    </div>
                                    <div class="ctx-row" v-if="ctxQueryRewriter.rewritten">
                                        <span class="ctx-label">Rewritten</span>
                                        <span class="ctx-value ctx-value-text">{{ ctxQueryRewriter.rewritten }}</span>
                                    </div>
                                </div>
                            </div>

                            <div class="ctx-card" v-if="ctxOrchestrator.goal">
                                <div class="ctx-card-header">
                                    <v-icon size="14" color="#00897b">mdi-sitemap-outline</v-icon>
                                    <span>Orchestrator</span>
                                </div>
                                <div class="ctx-card-body">
                                    <div class="ctx-row" v-if="ctxOrchestrator.goal">
                                        <span class="ctx-label">Goal</span>
                                        <span class="ctx-value ctx-value-text">{{ ctxOrchestrator.goal }}</span>
                                    </div>
                                    <div class="ctx-row-pair">
                                        <div class="ctx-pair" v-if="ctxOrchestrator.mode">
                                            <span class="ctx-label">Mode</span>
                                            <v-chip size="x-small" :color="ctxOrchestrator.mode === 'Parallel' ? 'teal' : 'blue'" variant="tonal">
                                                <v-icon size="10" start>{{ ctxOrchestrator.mode === 'Parallel' ? 'mdi-arrow-split-vertical' : 'mdi-arrow-right' }}</v-icon>
                                                {{ ctxOrchestrator.mode }}
                                            </v-chip>
                                        </div>
                                        <div class="ctx-pair" v-if="ctxOrchestrator.agentCount">
                                            <span class="ctx-label">Agents</span>
                                            <span class="ctx-value-num">{{ ctxOrchestrator.agentCount }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="ctx-card" v-if="ctxIntent.detected">
                                <div class="ctx-card-header">
                                    <v-icon size="14" color="#f57c00">mdi-target</v-icon>
                                    <span>Intent Detection</span>
                                </div>
                                <div class="ctx-card-body">
                                    <div class="ctx-row-inline">
                                        <span class="ctx-label">Detected</span>
                                        <v-chip size="x-small" color="orange" variant="tonal">{{ ctxIntent.detected }}</v-chip>
                                    </div>
                                    <div class="ctx-row-inline" v-if="ctxIntent.confidence != null">
                                        <span class="ctx-label">Confidence</span>
                                        <div class="ctx-confidence-bar">
                                            <div class="confidence-fill" :style="{ width: (ctxIntent.confidence * 100) + '%' }"></div>
                                        </div>
                                        <span class="ctx-confidence-num">{{ (ctxIntent.confidence * 100).toFixed(0) }}%</span>
                                    </div>
                                </div>
                            </div>

                            <div class="ctx-card" v-if="ctxdynamicRouter.detected">
                                <div class="ctx-card-header">
                                    <v-icon size="14" color="#0d9488">mdi-file-document-multiple</v-icon>
                                    <span>Dynamic Router</span>
                                </div>
                                <div class="ctx-card-body">
                                    <div class="ctx-row-inline">
                                        <span class="ctx-label">Intent</span>
                                        <v-chip size="x-small" color="teal" variant="tonal">{{ ctxdynamicRouter.detected }}</v-chip>
                                    </div>
                                    <div class="ctx-row-inline" v-if="ctxdynamicRouter.confidence != null">
                                        <span class="ctx-label">Confidence</span>
                                        <div class="ctx-confidence-bar">
                                            <div class="confidence-fill" :style="{ width: (ctxdynamicRouter.confidence * 100) + '%', backgroundColor: '#0d9488' }"></div>
                                        </div>
                                        <span class="ctx-confidence-num">{{ (ctxdynamicRouter.confidence * 100).toFixed(0) }}%</span>
                                    </div>
                                    <div class="ctx-row" v-if="ctxdynamicRouter.intentPath">
                                        <span class="ctx-label">Path</span>
                                        <span class="ctx-value ctx-value-text">{{ ctxdynamicRouter.intentPath }}</span>
                                    </div>
                                    <div class="ctx-row" v-if="ctxdynamicRouter.depth != null">
                                        <span class="ctx-label">Depth</span>
                                        <span class="ctx-value">{{ ctxdynamicRouter.depth }}</span>
                                    </div>
                                    <div class="ctx-row" v-if="ctxdynamicRouter.resolvedTags">
                                        <span class="ctx-label">Tags</span>
                                        <span class="ctx-value ctx-value-text">{{ ctxdynamicRouter.resolvedTags }}</span>
                                    </div>
                                    <div class="ctx-row-inline" v-if="ctxdynamicRouter.docCount > 0">
                                        <span class="ctx-label">Documents</span>
                                        <v-chip size="x-small" color="teal" variant="tonal">{{ ctxdynamicRouter.docCount }}</v-chip>
                                    </div>
                                </div>
                            </div>

                            <div class="ctx-card" v-if="ctxGuardrail.show">
                                <div class="ctx-card-header">
                                    <v-icon size="14" :color="ctxGuardrail.passed ? '#43a047' : '#e53935'">mdi-shield-check-outline</v-icon>
                                    <span>Guardrail</span>
                                </div>
                                <div class="ctx-card-body">
                                    <div class="ctx-row-inline">
                                        <span class="ctx-label">Status</span>
                                        <v-chip size="x-small" :color="ctxGuardrail.passed ? 'success' : 'error'" variant="flat">
                                            <v-icon size="10" start>{{ ctxGuardrail.passed ? 'mdi-check' : 'mdi-block-helper' }}</v-icon>
                                            {{ ctxGuardrail.passed ? 'Passed' : 'Blocked' }}
                                        </v-chip>
                                    </div>
                                </div>
                            </div>

                            <div class="ctx-card" v-if="ctxProducts.count > 0">
                                <div class="ctx-card-header">
                                    <v-icon size="14" color="#5c6bc0">mdi-package-variant</v-icon>
                                    <span>Products</span>
                                    <v-chip size="x-small" color="indigo" variant="tonal" class="ml-auto">{{ ctxProducts.count }}</v-chip>
                                </div>
                                <div class="ctx-card-body">
                                    <div class="ctx-product-list">
                                        <div v-for="(prod, pi) in ctxProducts.items.slice(0, productsShowAll ? undefined : 3)" :key="pi"
                                            class="ctx-product-item">
                                            <span class="product-name">{{ getProductName(prod) }}</span>
                                        </div>
                                        <button v-if="ctxProducts.count > 3" class="ctx-toggle" @click="productsShowAll = !productsShowAll">
                                            {{ productsShowAll ? 'Show less' : `+ ${ctxProducts.count - 3} more` }}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div class="ctx-card" v-if="ctxFlowPath">
                                <div class="ctx-card-header">
                                    <v-icon size="14" color="#546e7a">mdi-ray-start-arrow</v-icon>
                                    <span>Flow Path</span>
                                </div>
                                <div class="ctx-card-body">
                                    <div class="ctx-flow-path">
                                        <template v-for="(segment, si) in flowPathSegments" :key="si">
                                            <span class="flow-segment">{{ segment }}</span>
                                            <v-icon v-if="si < flowPathSegments.length - 1" size="10" color="#bbb" class="flow-arrow">mdi-chevron-right</v-icon>
                                        </template>
                                    </div>
                                </div>
                            </div>

                            <div class="ctx-raw-section">
                                <button class="ctx-raw-toggle" @click="showRawVars = !showRawVars">
                                    <v-icon size="12" class="mr-1">{{ showRawVars ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                                    Raw Variables ({{ Object.keys(currentVariables).length }})
                                </button>
                                <div v-if="showRawVars" class="ctx-raw-content">
                                    <div class="detail-code">{{ formatJson(currentVariables) }}</div>
                                </div>
                            </div>
                        </div>
                    </v-tabs-window-item>
                </v-tabs-window>
            </div>
        </div>
    </transition>
</template>

<script>
export default {
    name: 'DebugInspector',
    props: {
        visible: { type: Boolean, default: false },
        steps: { type: Array, default: () => [] },
        currentNodeId: { type: String, default: '' },
        currentVariables: { type: Object, default: () => ({}) },
        changedVariables: { type: Set, default: () => new Set() },
        status: { type: String, default: '' },
        lastReply: { type: String, default: '' },
    },
    emits: ['close', 'select-step', 'focus-node', 'patch-variable', 'set-next'],
    data() {
        return {
            collapsed: false,
            activeTab: 'steps',
            selectedStepIndex: -1,
            aiResponseExpanded: false,
            productsShowAll: false,
            showRawVars: false,
            editedUserMessage: '',
            userMessageDirty: false,
            initialUserMessage: '',
            panelWidth: 340,
            panelHeight: null,
            resizing: false,
            resizeDirection: null,
            resizeStartX: 0,
            resizeStartY: 0,
            resizeStartW: 0,
            resizeStartH: 0,
        };
    },
    computed: {
        isPaused() {
            return this.status === 'paused';
        },
        inspectorStyle() {
            if (this.collapsed) return {};
            const style = { width: this.panelWidth + 'px' };
            if (this.panelHeight) {
                style.height = this.panelHeight + 'px';
                style.maxHeight = this.panelHeight + 'px';
            }
            return style;
        },
        ctxInput() {
            // Only show input from the user message (captured once from messageInput node)
            return { message: this.initialUserMessage };
        },
        ctxAI() {
            // When flow completes, use the final reply; otherwise use live aiResponse from variables
            const isCompleted = this.status === 'completed';
            const response = isCompleted && this.lastReply ? this.lastReply : this.getVar('aiResponse');
            return {
                response,
                model: this.getVar('aiModel'),
                tokens: this.getVar('tokenUsage'),
            };
        },
        ctxQueryRewriter() {
            return {
                mode: this.getVar('queryRewriterMode'),
                original: this.getVar('originalUserMessage'),
                continueQuestion: this.getVar('queryRewriterContinueQuestion'),
                rewritten: this.getVar('queryRewriterMessage'),
            };
        },
        aiResponseTextLength() {
            const resp = this.ctxAI.response;
            if (!resp) return 0;
            return typeof resp === 'string' ? resp.length : JSON.stringify(resp).length;
        },
        ctxOrchestrator() {
            return {
                goal: this.getVar('orchestratorGoal'),
                mode: this.getVar('executionMode'),
                agentCount: this.getVar('agentCount'),
            };
        },
        ctxIntent() {
            return {
                detected: this.getVar('advRouterDetectedIntent'),
                confidence: this.getVar('advRouterConfidence'),
            };
        },
        ctxdynamicRouter() {
            const docs = this.getVar('dynamicRouterDocuments');
            return {
                detected: this.getVar('dynamicRouterDetectedIntent'),
                confidence: this.getVar('dynamicRouterConfidence'),
                intentPath: this.getVar('dynamicRouterIntentPath'),
                depth: this.getVar('dynamicRouterDepth'),
                resolvedTags: this.getVar('dynamicRouterResolvedTags'),
                docCount: Array.isArray(docs) ? docs.length : 0,
            };
        },
        ctxGuardrail() {
            const passed = this.getVar('guardrailPassed');
            const blocked = this.getVar('guardrailBlocked');
            return {
                show: passed != null || blocked != null,
                passed: passed === true || blocked === false,
            };
        },
        ctxProducts() {
            const products = this.getVar('products');
            const items = Array.isArray(products) ? products : [];
            return { count: items.length, items };
        },
        ctxFlowPath() {
            return this.getVar('workFlow');
        },
        flowPathSegments() {
            const wf = this.ctxFlowPath;
            if (!wf) return [];
            return String(wf).split('>').map(s => s.trim()).filter(Boolean);
        },
    },
    watch: {
        steps: {
            handler() {
                if (this.selectedStepIndex >= this.steps.length) {
                    this.selectedStepIndex = -1;
                }
                this.$nextTick(() => {
                    const list = this.$refs.stepsList;
                    if (list) list.scrollTop = list.scrollHeight;
                });
            },
            deep: true,
        },
        currentVariables: {
            handler(vars) {
                this.aiResponseExpanded = false;
                const msg = vars?.userMessage;
                // Capture initial user message once (first non-empty value) — never overwritten
                if (!this.initialUserMessage && msg) {
                    this.initialUserMessage = String(msg);
                }
                // Update editable input only when not user-dirty and message exists
                if (msg !== undefined && msg !== null && !this.userMessageDirty) {
                    this.editedUserMessage = String(msg);
                }
            },
            deep: true,
        },
        status(newStatus) {
            if (newStatus !== 'paused') {
                this.userMessageDirty = false;
            }
            // Reset captured initial message only when starting a fresh debug session
            if (newStatus === 'ready') {
                this.initialUserMessage = '';
            }
        },
        visible(val) {
            const flow = document.querySelector('.vue-flow');
            if (flow) {
                flow.classList.toggle('debug-mode-active', val);
            }
        },
    },
    methods: {
        getVar(key) {
            if (!this.currentVariables) return null;
            const val = this.currentVariables[key];
            return val !== undefined && val !== null ? val : null;
        },
        formatNumber(n) {
            if (n == null) return '0';
            return Number(n).toLocaleString();
        },
        getProductName(prod) {
            if (!prod) return 'Unknown product';
            return prod.ProductName || prod.productName || prod.Name || prod.name || prod.Title || prod.title || JSON.stringify(prod).substring(0, 60);
        },
        selectStep(idx) {
            this.selectedStepIndex = this.selectedStepIndex === idx ? -1 : idx;
            const step = this.steps[idx];
            const targetNodeId = this.getStepFocusNodeId(step);
            if (targetNodeId) {
                this.$emit('focus-node', targetNodeId);
            }
        },
        getStepFocusNodeId(step) {
            if (!step?.nodeId) return '';

            if (step.isAiParallelSummary) {
                const activeParallelLog = this.getParallelItems(step).find(item => {
                    const itemNodeId = item?.nodeId || item?.NodeId;
                    const itemStatus = item?.status;
                    return itemNodeId && (itemNodeId === this.currentNodeId || itemStatus === 'paused' || itemStatus === 'running');
                });
                const fallbackParallelLog = this.getParallelItems(step).find(item => item?.nodeId || item?.NodeId);
                return activeParallelLog?.nodeId || activeParallelLog?.NodeId || fallbackParallelLog?.nodeId || fallbackParallelLog?.NodeId || step.focusNodeId || step.nodeId;
            }

            return step.focusNodeId || step.sourceNodeId || step.nodeId;
        },
        getStepDisplayIndex(idx) {
            let displayIndex = 0;
            for (let i = 0; i <= idx; i++) {
                if (!this.steps[i]?.isSubStep) displayIndex++;
            }
            return displayIndex;
        },
        shouldShowStepVariables(step) {
            return !step?.isAiParallelSummary && !!(step?.contextVariables && Object.keys(step.contextVariables).length);
        },
        hasStepDetail(step) {
            return step.outputPath || step.gptReply || step.errorMessage || step.output ||
                this.getParallelItems(step).length ||
                step.agentName ||
                this.shouldShowStepVariables(step);
        },
        getToolsUsed(step) {
            if (!step.output) return [];
            const tools = step.output.ToolsUsed || step.output.toolsUsed;
            return Array.isArray(tools) ? tools : [];
        },
        getStepsExecuted(step) {
            if (!step.output) return [];
            const steps = step.output.StepsExecuted || step.output.stepsExecuted;
            return Array.isArray(steps) ? steps : [];
        },
        getNodeTypeColor(type) {
            const colors = {
                messageInput: 'blue', model: 'purple', response: 'green',
                intent: 'orange', smartIntent: 'orange', router: 'orange',
                advancedRouter: 'orange', dynamicRouter: 'teal', orchestrator: 'indigo', agent: 'deep-purple',
                aiParallel: 'indigo', queryRewriter: 'deep-purple',
                documents: 'teal', dynamicKnowledge: 'teal', products: 'teal', condition: 'amber',
                delay: 'grey', email: 'pink', apiTrigger: 'cyan',
            };
            return colors[type] || 'grey';
        },
        getParallelResults(step) {
            const output = step?.output || {};
            const results = output.parallelResults || output.ParallelResults || [];
            return Array.isArray(results) ? results : [];
        },
        getParallelModelLogs(step) {
            const output = step?.output || {};
            const logs = step?.parallelModelLogs || output.parallelModelLogs || output.ParallelModelLogs || [];
            return Array.isArray(logs) ? logs : [];
        },
        getParallelItems(step) {
            const logs = this.getParallelModelLogs(step);
            const results = this.getParallelResults(step);
            if (!logs.length) return results;

            const merged = logs.map(log => {
                const result = results.find(item => (item?.NodeId || item?.nodeId) === log.nodeId);
                return result ? { ...result, ...log, finalResult: result } : log;
            });

            results.forEach(result => {
                const resultNodeId = result?.NodeId || result?.nodeId;
                if (!merged.some(item => item.nodeId === resultNodeId || item.NodeId === resultNodeId)) {
                    merged.push(result);
                }
            });

            return merged;
        },
        getParallelResultName(result, index) {
            return result?.agentName || result?.NodeName || result?.nodeName || result?.NodeId || result?.nodeId || `Model ${index + 1}`;
        },
        getParallelResultSuccess(result) {
            const success = result?.Success ?? result?.success;
            return success !== false;
        },
        getParallelResultStatus(result) {
            return result?.status || (this.getParallelResultSuccess(result) ? 'success' : 'error');
        },
        getParallelResultColor(result) {
            const status = this.getParallelResultStatus(result);
            if (status === 'running') return 'blue';
            if (status === 'paused') return 'orange';
            return this.getParallelResultSuccess(result) ? 'green' : 'red';
        },
        getParallelResultIcon(result) {
            const status = this.getParallelResultStatus(result);
            if (status === 'running' || status === 'paused') return 'mdi-loading';
            return this.getParallelResultSuccess(result) ? 'mdi-check-circle' : 'mdi-alert-circle';
        },
        getParallelResultDuration(result) {
            return result?.durationMs || result?.DurationMs;
        },
        getParallelResultResponse(result) {
            const reply = result?.gptReply || result?.GptReply || result?.GPTReply;
            if (typeof reply === 'string') return reply;
            if (reply?.replytext) return reply.replytext;

            const finalResult = result?.finalResult || {};
            const candidates = [
                result?.Response,
                result?.response,
                result?.output?.Response,
                result?.output?.response,
                result?.output?.replytext,
                finalResult?.Response,
                finalResult?.response,
                finalResult?.GPTReply?.replytext,
                result?.Data?.replytext,
                result?.data?.replytext,
            ];
            const response = candidates.find(value => value !== undefined && value !== null && value !== '');
            if (typeof response === 'string') return response;
            if (response !== undefined && response !== null) return this.formatJson(response);
            return '';
        },
        getParallelResultError(result) {
            return result?.errorMessage || result?.ErrorMessage || result?.Message || result?.message || '';
        },
        isPausedInsideParallel() {
            if (!this.isPaused || !this.currentNodeId) return false;
            return this.steps.some(step => this.getParallelModelLogs(step).some(log => log.nodeId === this.currentNodeId && log.status === 'paused'));
        },
        canJumpToStep(step) {
            if (!this.isPaused || !step?.nodeId || step.isSubStep || step.isParallelModelStep) return false;
            if (this.isPausedInsideParallel()) return !!step.isAiParallelSummary;
            return true;
        },
        formatDuration(ms) {
            if (!ms && ms !== 0) return '';
            if (ms < 1000) return `${ms}ms`;
            return `${(ms / 1000).toFixed(1)}s`;
        },
        formatJson(obj) {
            try { return JSON.stringify(obj, null, 2); }
            catch { return String(obj); }
        },
        onUserMessageInput(val) {
            this.editedUserMessage = val;
            this.userMessageDirty = true;
            this.$emit('patch-variable', { key: 'userMessage', value: val });
        },
        emitSetNext(step) {
            if (step?.nodeId) {
                const focusNodeId = this.getStepFocusNodeId(step) || step.focusNodeId || step.sourceNodeId;
                this.$emit('set-next', { nodeId: step.nodeId, stepIndex: step.stepIndex, sourceNodeId: step.sourceNodeId, focusNodeId });
            }
        },
        startResize(e, direction) {
            this.resizing = true;
            this.resizeDirection = direction;
            this.resizeStartX = e.clientX;
            this.resizeStartY = e.clientY;
            this.resizeStartW = this.panelWidth;
            this.resizeStartH = this.panelHeight || this.$el.offsetHeight;
            document.addEventListener('mousemove', this.onResize);
            document.addEventListener('mouseup', this.stopResize);
            const nonRightCursor = direction === 'bottom' ? 'ns-resize' : 'nwse-resize';
            document.body.style.cursor = direction === 'right' ? 'ew-resize' : nonRightCursor;
            document.body.style.userSelect = 'none';
        },
        onResize(e) {
            if (!this.resizing) return;
            const dx = e.clientX - this.resizeStartX;
            const dy = e.clientY - this.resizeStartY;
            const rect = this.$el.getBoundingClientRect();
            if (this.resizeDirection === 'right' || this.resizeDirection === 'corner') {
                const maxW = Math.min(700, window.innerWidth - rect.left - 12);
                this.panelWidth = Math.max(280, Math.min(maxW, this.resizeStartW + dx));
            }
            if (this.resizeDirection === 'bottom' || this.resizeDirection === 'corner') {
                const maxH = Math.min(window.innerHeight - 100, window.innerHeight - rect.top - 12);
                this.panelHeight = Math.max(200, Math.min(maxH, this.resizeStartH + dy));
            }
        },
        stopResize() {
            this.resizing = false;
            this.resizeDirection = null;
            document.removeEventListener('mousemove', this.onResize);
            document.removeEventListener('mouseup', this.stopResize);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        },
    },
    beforeUnmount() {
        document.removeEventListener('mousemove', this.onResize);
        document.removeEventListener('mouseup', this.stopResize);
        // Clean up debug-mode-active class when component is destroyed
        const flow = document.querySelector('.vue-flow');
        if (flow) flow.classList.remove('debug-mode-active');
    },
};
</script>

<style scoped>
.debug-inspector {
    position: absolute;
    top: 145px;
    left: 20px;
    z-index: 100;
    width: 340px;
    max-height: calc(100vh - 250px);
    background: rgba(255, 255, 255, 0.97);
    backdrop-filter: blur(12px);
    border-radius: 12px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.10), 0 0 0 1px rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.resize-handle { position: absolute; z-index: 10; }
.resize-right { top: 0; right: 0; width: 5px; height: 100%; cursor: ew-resize; }
.resize-bottom { bottom: 0; left: 0; width: calc(100% - 14px); height: 5px; cursor: ns-resize; }
.resize-corner { bottom: 0; right: 0; width: 18px; height: 18px; cursor: nwse-resize; display: flex; align-items: flex-end; justify-content: flex-end; }
.resize-corner-icon { color: rgba(0,0,0,0.4); pointer-events: none; right: 3px; bottom: 3px; }
.resize-corner:hover .resize-corner-icon { color: rgba(25, 118, 210, 0.5); }
.resize-handle:hover { background: rgba(25, 118, 210, 0.08); }

.debug-inspector.inspector-collapsed { width: 48px; cursor: pointer; }
.di-header-collapsed { justify-content: center !important; padding: 10px 8px !important; }

.di-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    border-bottom: 1px solid #f0f0f0;
    background: linear-gradient(135deg, #f8f9ff, #f0f4ff);
    flex-shrink: 0;
}

.di-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #333;
}

.di-header-actions { display: flex; align-items: center; gap: 2px; }
.di-body { flex: 1; overflow: hidden; display: flex; flex-direction: column; }
.di-tabs { flex-shrink: 0; }
.di-tabs :deep(.v-tab) { font-size: 11px; min-width: 0; text-transform: none; letter-spacing: 0; }
.di-tab-content { flex: 1; overflow-y: auto; }

.di-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
}

.di-steps-list { padding: 4px; }

.di-step-item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 6px 8px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.15s;
    margin-bottom: 2px;
}

.step-expand-icon { flex-shrink: 0; margin-top: 4px; opacity: 0.5; }
.di-step-item:hover .step-expand-icon { opacity: 1; }
.di-step-item:hover { background: #f5f5f5; }
.di-step-item.step-selected { background: #e3f2fd; }
.di-step-item.step-current { background: #e8f5e9; }
.di-step-item.step-error { background: #fff8f8; }
.di-step-item.step-paused { background: #e3f2fd; }

.di-step-item.step-sub {
    padding-left: 24px;
    border-left: 2px solid #ce93d8;
    margin-left: 12px;
    background: #faf5ff;
}
.di-step-item.step-sub:hover { background: #f3e5f5; }
.di-step-item.step-sub.step-selected { background: #e8daef; }
.di-step-item.step-sub.step-error { background: #fce4ec; }

.di-step-detail-inline.detail-agent {
    margin-left: 42px;
    border-color: #e1bee7;
    background: linear-gradient(135deg, #faf5ff, #fcf8ff);
}

.step-indicator { flex-shrink: 0; margin-top: 2px; }
.step-content { flex: 1; min-width: 0; }
.step-name { font-size: 12px; font-weight: 500; color: #333; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.step-index { color: #6b6b6b; font-size: 11px; margin-right: 2px; }
.step-meta { display: flex; align-items: center; gap: 4px; margin-top: 2px; }
.step-duration { font-size: 10px; color: #757575; }

.di-step-detail-inline {
    margin: 0 4px 6px 30px;
    padding: 8px 10px;
    background: linear-gradient(135deg, #f0f7ff, #fafcff);
    border: 1px solid #e3f2fd;
    border-radius: 8px;
    animation: detailSlideIn 0.2s ease;
}

.di-no-detail { padding: 4px 0; text-align: center; }

@keyframes detailSlideIn {
    from { opacity: 0; max-height: 0; padding: 0 10px; }
    to { opacity: 1; max-height: 500px; padding: 8px 10px; }
}

.detail-section { margin-bottom: 8px; }
.detail-label { font-size: 10px; font-weight: 600; color: #6f6f6f; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }

.detail-code {
    font-family: 'Fira Code', 'Cascadia Code', monospace;
    font-size: 11px;
    background: #f8f9fa;
    border: 1px solid #eee;
    border-radius: 6px;
    padding: 8px;
    max-height: 150px;
    overflow-y: auto;
    white-space: pre-wrap;
    word-break: break-all;
    color: #333;
}

.detail-code.error-code { background: #fff5f5; border-color: #ffcdd2; color: #c62828; }
.tools-used-list { display: flex; flex-wrap: wrap; gap: 2px; }
.agent-steps-list { display: flex; flex-direction: column; gap: 3px; }
.agent-exec-step { display: flex; align-items: center; font-size: 11px; color: #555; padding: 2px 4px; border-radius: 4px; background: #fafafa; }
.exec-step-tool { font-weight: 600; color: #1871ca; margin-right: 6px; white-space: nowrap; }
.exec-step-desc { color: #666; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.parallel-result-list { display: flex; flex-direction: column; gap: 5px; }
.parallel-result-item { display: flex; flex-direction: column; gap: 4px; font-size: 11px; color: #555; padding: 5px 6px; border-radius: 4px; background: #f3f5fb; }
.parallel-result-header { display: flex; align-items: center; min-width: 0; }
.parallel-result-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.parallel-result-duration { color: #6f6f6f; font-size: 10px; margin-left: 6px; flex-shrink: 0; }
.parallel-result-response { font-family: 'Fira Code', 'Cascadia Code', monospace; font-size: 10.5px; line-height: 1.45; color: #333; background: rgba(255, 255, 255, 0.72); border: 1px solid rgba(63, 81, 181, 0.08); border-radius: 4px; padding: 5px 6px; max-height: 110px; overflow-y: auto; white-space: pre-wrap; overflow-wrap: break-word; }
.parallel-result-error { color: #c62828; border-color: #ffcdd2; background: #fff5f5; }

.ctx-container { display: flex; flex-direction: column; gap: 6px; padding: 8px; }

.ctx-card {
    background: #fff;
    border: 1px solid #f0f0f0;
    border-radius: 10px;
    overflow: hidden;
    transition: border-color 0.2s;
}
.ctx-card:hover { border-color: #e0e0e0; }

.ctx-card-header {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 10px;
    background: #fafbfc;
    border-bottom: 1px solid #f5f5f5;
    font-size: 11.5px;
    font-weight: 600;
    color: #444;
}

.ctx-card-body { padding: 8px 10px; display: flex; flex-direction: column; gap: 8px; }
.ctx-row { display: flex; flex-direction: column; gap: 2px; }
.ctx-row-inline { display: flex; align-items: center; gap: 8px; min-height: 24px; }

.ctx-label {
    font-size: 10px;
    font-weight: 600;
    color: #757575;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    flex-shrink: 0;
    min-width: 56px;
}

.ctx-value-text { font-size: 12px; color: #333; line-height: 1.45; overflow-wrap: break-word; }
.ctx-editable-input { font-size: 12px; }
.ctx-editable-input :deep(.v-field) { font-size: 12px; min-height: 36px; }

.ctx-value-prose {
    font-size: 12px;
    color: #333;
    line-height: 1.45;
    overflow-wrap: break-word;
    white-space: pre-wrap;
    max-height: 120px;
    overflow-y: auto;
    transition: max-height 0.3s ease;
}
.ctx-value-prose.ctx-expanded { max-height: 400px; }

.ctx-toggle {
    border: none;
    background: none;
    color: #1976d2;
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    padding: 2px 0;
    text-align: left;
}
.ctx-toggle:hover { text-decoration: underline; }

.ctx-row-pair { display: flex; gap: 16px; }
.ctx-pair { display: flex; flex-direction: column; gap: 3px; flex: 1; }
.ctx-value-num { font-size: 18px; font-weight: 700; color: #333; line-height: 1; }

.ctx-token-group { display: flex; align-items: center; gap: 8px; }
.ctx-token { font-size: 11px; font-weight: 600; font-family: 'Fira Code', monospace; }
.ctx-token.total { color: #333; }
.ctx-token.prompt { color: #1976d2; }
.ctx-token.completion { color: #37843b; }

.ctx-confidence-bar { flex: 1; height: 4px; background: #eee; border-radius: 2px; overflow: hidden; }
.confidence-fill { height: 100%; background: linear-gradient(90deg, #ffb74d, #ff9800); border-radius: 2px; transition: width 0.4s ease; }
.ctx-confidence-num { font-size: 11px; font-weight: 600; color: #b65c00; flex-shrink: 0; }

.ctx-product-list { display: flex; flex-direction: column; gap: 2px; }
.ctx-product-item { font-size: 12px; color: #444; padding: 3px 6px; background: #f9f9fb; border-radius: 4px; }
.product-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: block; }

.ctx-flow-path { display: flex; align-items: center; flex-wrap: wrap; gap: 2px; }
.flow-segment { font-size: 11px; color: #555; padding: 2px 6px; background: #f0f4ff; border-radius: 4px; white-space: nowrap; }
.flow-arrow { flex-shrink: 0; }

.ctx-raw-section { border-top: 1px solid #f0f0f0; margin-top: 4px; }
.ctx-raw-toggle {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 8px;
    background: none;
    border: none;
    color: #757575;
    font-size: 10.5px;
    cursor: pointer;
    transition: color 0.15s;
}
.ctx-raw-toggle:hover { color: #666; }
.ctx-raw-content { padding: 0 8px 8px; animation: detailSlideIn 0.2s ease; }

.spin-animation { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

.di-steps-list::-webkit-scrollbar,
.di-tab-content::-webkit-scrollbar,
.detail-code::-webkit-scrollbar,
.ctx-value-prose::-webkit-scrollbar { width: 4px; }
.di-steps-list::-webkit-scrollbar-thumb,
.di-tab-content::-webkit-scrollbar-thumb,
.detail-code::-webkit-scrollbar-thumb,
.ctx-value-prose::-webkit-scrollbar-thumb { background: #ddd; border-radius: 2px; }

.debug-inspector-slide-enter-active,
.debug-inspector-slide-leave-active { transition: all 0.3s ease; }
.debug-inspector-slide-enter-from,
.debug-inspector-slide-leave-to { opacity: 0; transform: translateX(-20px); }

@media (max-width: 768px) {
    .debug-inspector { top: 80px; left: 8px; }
}
</style>

<style>
/* Global: debug border overlay on vue-flow when debug mode is active */
.vue-flow.debug-mode-active::after {
    content: '';
    position: absolute;
    top: 37px;
    left: 0;
    width: 100%;
    height: calc(100% - 37px);
    border: 2px solid #ff9900;
    box-shadow: inset 0 0 0 4px #f4d3a4;
    border-radius: 16px;
    box-sizing: border-box;
    pointer-events: none;
    z-index: 10;
}
</style>